import type { Identity } from '../types/auth';

/**
 * Logbook permission nodes, mirroring the Loglibrarian credential roles the backend gates on
 * (argus-backend src/routes/logbook.ts). `argus.logbook.super` grants all four, and
 * `SPY_Super_admin` keeps the usual Argus override. There is no development bypass: evidence access
 * is auditable, so it is never granted by the environment the page happens to be running in.
 */

export const LOGBOOK_PERMISSIONS = {
  read: 'argus.logbook.read',
  export: 'argus.logbook.export',
  protect: 'argus.logbook.protect',
  admin: 'argus.logbook.admin',
  super: 'argus.logbook.super',
} as const;

export type LogbookCapability = 'read' | 'export' | 'protect' | 'admin';

function nodes(identity: Identity | null | undefined): string[] {
  const permissions = identity?.raw?.custom?.permissions;
  return Array.isArray(permissions) ? permissions : [];
}

function isSuper(identity: Identity | null | undefined): boolean {
  const groups = identity?.raw?.custom?.groups;
  return (Array.isArray(groups) && groups.includes('SPY_Super_admin')) || nodes(identity).includes(LOGBOOK_PERMISSIONS.super);
}

export function hasLogbookCapability(identity: Identity | null | undefined, capability: LogbookCapability): boolean {
  if (isSuper(identity)) return true;
  const held = nodes(identity);
  if (held.includes(LOGBOOK_PERMISSIONS[capability])) return true;
  // Reading is implied by the narrower grants: nobody can preserve or export what they may not read.
  return capability === 'read'
    && (held.includes(LOGBOOK_PERMISSIONS.export) || held.includes(LOGBOOK_PERMISSIONS.protect) || held.includes(LOGBOOK_PERMISSIONS.admin));
}

export const canViewLogbook = (identity: Identity | null | undefined): boolean => hasLogbookCapability(identity, 'read');
export const canExportLogbook = (identity: Identity | null | undefined): boolean => hasLogbookCapability(identity, 'export');
export const canProtectLogbook = (identity: Identity | null | undefined): boolean => hasLogbookCapability(identity, 'protect');
export const canAdministerLogbook = (identity: Identity | null | undefined): boolean => hasLogbookCapability(identity, 'admin');
